import React from 'react';
import {css} from 'aphrodite/no-important';

import CoreMetrics, {createExpandableMediaQueryStylesheet} from '../core/Metrics';


const getWindowColumnCount = (screenName) => {
    return parseInt(CoreMetrics.Grid.ColumnCount()[screenName], 10);
};

const getActualColumnCount = (windowSize, props) => {
    let size = getWindowColumnCount(windowSize);
    if (props[`size${windowSize}`] !== undefined) {
        size = props[`size${windowSize}`];
    } else if (props.size !== undefined) {
        size = props.size
    }

    return size;
};

const getColumnName = (rowIndex, columnIndex) => {
    return `row${rowIndex}column${columnIndex}`;
};

const checkIfIsColumn = (child) => {
    return child.type === CssGridColumn || child.type === CssGridSpace;
};

const getGridColumnString = (columnNum) => `repeat(${columnNum}, 1fr)`;


export class CssGridSpace extends React.Component {
    render() {
        return null;
    }
}


export class CssGridColumn extends React.Component {
    render() {
        return <div
            style={{gridArea: this.props.gridArea}}
            className={css(this.props.styles)}
        >
            {this.props.children}
        </div>;
    }
}
CssGridColumn.defaultProps = {
    styles: [],
};


export class CssGridRow extends React.Component {
    render() {
        return this.props.children;
    }
}


class CssGridContainer extends React.Component {
    static defaultProps = {
        styles: [],
    }

    getTemplateAreasString = () => {
        const {children, columnsWithin} = this.props;

        // If the grid is being broken up into columns via autoflow,
        // we explicitly do not want grid area names on the columns.
        // That breaks autoflow.
        if (columnsWithin) {
            return null;
        }

        const areas = React.Children.toArray(children)
            .filter((child) => child.type === CssGridRow)
            .map((row, rowIndex) => {
                const columns = React.Children.toArray(row.props.children)
                    .filter((child) => checkIfIsColumn(child))
                    .map((column, columnIndex) => {
                        let columnName = ".";
                        if (column.type === CssGridColumn) {
                            columnName = getColumnName(rowIndex, columnIndex);
                        }

                        const gridColumns = [];
                        for (let i = 0; i < column.props.size; i++) {
                            gridColumns.push(columnName);
                        }

                        return gridColumns.join(" ");
                    }).join(" ");

                return `"${columns}"`;
            });

        // Empty rows appear as empty strings, which mess with the grid's head.
        return areas.filter((area) => area !== '""').join(" ");
    }

    getInlineGridStyles = () => {
        const {columnsWithin, size} = this.props;

        const inlineStyles = {
            gridTemplateAreas: this.getTemplateAreasString(),
        };

        // Overriding the default gridTemplateColumns works here because we're
        // using the version of Aphrodite that doesn't have !important
        // statements.
        // In webapp, we might have to move the default gridTemplateColumns
        // styling media queries here so we can override them more easily.
        if (columnsWithin) {
            inlineStyles["gridTemplateColumns"] =
                getGridColumnString(columnsWithin);
        } else if (size) {
            inlineStyles["gridTemplateColumns"] = getGridColumnString(size);
        }

        return inlineStyles;
    }

    render() {
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                maxWidth: CoreMetrics.Grid.FixedWidth,
                gridColumnGap: CoreMetrics.Grid.HorizontalGutter,
                justifyItems: "stretch",
                justifyContent: "stretch",
                gridTemplateRows: "auto",
                margin: "0 auto",
                gridAutoFlow: "row",

                [CoreMetrics.MediaQuery().Desktop]: {
                    gridTemplateColumns:
                        getGridColumnString(getWindowColumnCount("Desktop")),
                },

                [CoreMetrics.MediaQuery().Tablet]: {
                    gridTemplateColumns:
                        getGridColumnString(getWindowColumnCount("Tablet")),
                },

                [CoreMetrics.MediaQuery().Phone]: {
                    gridTemplateColumns:
                        getGridColumnString(getWindowColumnCount("Phone")),
                },
            },
        });

        return (
            <div
                className={css(styles.container, ...this.props.styles)}
                style={this.getInlineGridStyles()}
            >
                {this.props.children}
            </div>
        );
    }
}


export class CssGrid extends React.Component {
    static defaultProps = {
        styles: [],
    }

    getColumnsForWindowWidth(row, rowIndex, windowSize) {
        const columns = React.Children.toArray(row.props.children)
            .filter((child) => checkIfIsColumn(child))
            .filter((child) => {
                const explicitSize = child.props[`size${windowSize}`];
                const fallbackSize = child.props.size;

                if (explicitSize === 0) {
                    return false;
                }
                if (!explicitSize && fallbackSize === 0) {
                    return false;
                }
                return true;
            })
            .map((column, columnIndex) => {
                const containerColumnCount = getActualColumnCount(
                    windowSize, column.props);

                const size = this.props.columnsWithin
                    ? this.props.columnsWithin
                    : containerColumnCount;

                // We always want a unique key to avoid React errors.
                const key = getColumnName(rowIndex, columnIndex);
                // If the grid is being broken up into columns via autoflow,
                // we explicitly do not want grid area names on the columns.
                // That breaks autoflow.
                const columnName = this.props.columnsWithin
                    ? null
                    : key;

                if (column.type === CssGridColumn) {
                    return <CssGridColumn
                        size={size}
                        key={key}
                        gridArea={columnName}
                        styles={column.props.styles}
                    >
                        {column}
                    </CssGridColumn>;
                } else {
                    // We could technically use a CssGridColumn here, but it's
                    // easier for debugging if you can see in the DOM that
                    // this is a CssGridSpace.
                    return <CssGridSpace
                        size={size}
                        key={key}
                        gridArea={columnName}
                    >
                        {column}
                    </CssGridSpace>;
                }
            });

        return <CssGridRow key={`row${rowIndex}${windowSize}`}>
            {columns}
        </CssGridRow>;
    }

    getGridContainerForWindowSize(rows, windowSize) {
        const styles = createExpandableMediaQueryStylesheet({
            layoutPhone: {
                display: "none",

                [CoreMetrics.MediaQuery().Phone]: {
                    display: "grid",
                },
            },
            layoutTablet: {
                display: "none",

                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "grid",
                },
            },
            layoutDesktop: {
                display: "none",

                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "grid",
                },
            },
        });

        const size = getActualColumnCount(windowSize, this.props);

        return <CssGridContainer
            styles={[styles[`layout${windowSize}`], ...this.props.styles]}
            key={`cssGrid${windowSize}`}
            columnsWithin={this.props.columnsWithin}
            size={size}
        >
            {rows.map((row, rowIndex) => {
                return this.getColumnsForWindowWidth(row, rowIndex, windowSize);
            })}
        </CssGridContainer>;
    }

    render() {

        const {children} = this.props;
        const rows = React.Children.toArray(children)
            .filter((child) => child.type === CssGridRow);

        return <div>
            {Object.keys(CoreMetrics.DeviceScreens()).map((windowSize) => {
                return this.getGridContainerForWindowSize(rows, windowSize);
            })}
        </div>;
    }
}



// WEBPACK FOOTER //
// ./src/layouts/CssGrid.jsx